import React from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { breedRelationsResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import HorizontalSlider from 'dpl/components/HorizontalSlider';
import SmartImage from 'dpl/components/SmartImage';
import SmartLink from 'dpl/components/SmartLink';
import ClickableDiv from 'dpl/common/components/ClickableDiv';

const smartImageProps = {
  Tag: 'div',
  className: 'w-100 h-100 bg-center cover',
  crop: true,
  loadingClass: 'bg-light-gray',
  lazy: true
};

interface RelatedBreedsModuleProps {
  className?: string | null;
  heading?: string;
}

export default function RelatedBreedsModule({
  className,
  heading
}: RelatedBreedsModuleProps) {
  const { server, queryParams } = useSelector((state: any) => state);
  const { breeds = [{}], citySlug = '', preappliedFilters } = server;

  const breedId = server.breedId || queryParams.breed_id || breeds[0]?.id;
  const stateSlug = preappliedFilters
    ? preappliedFilters.stateSlug
    : server.stateSlug;

  const {
    value: { data: relatedBreeds }
  } = useResourceQuery({
    ...breedRelationsResourceDefinition(breedId, citySlug, stateSlug),
    // @ts-ignore
    auto: Boolean(breedId)
  });

  if (!relatedBreeds.length) {
    return null;
  }

  return (
    <div className={classnames('RelatedBreedsModule', className)}>
      {heading && (
        <h5 className="font-20 md:font-24 fw-medium mb6 mb8-md">{heading}</h5>
      )}
      <HorizontalSlider
        containerClassName="RelatedBreedsModuleSlider__container flex flex-nowrap gap-8 md:gap-24 ph6 ph0-sm"
        className="RelatedBreedsModuleSlider nl6 nr6 nl0-sm nr0-sm"
        iconClassName="secondary bg-white ph2 pv2 br-100 ba b--hairline"
        iconName="fetch-chevron"
      >
        {relatedBreeds.map((breed: any) => {
          const {
            availability_count_text: availabilityCountText,
            breed_overview_page_url: breedOverviewPageUrl,
            id,
            name,
            secondary_photo_url: photoUrl,
            url
          } = breed;

          return (
            <div className="flex ws-normal" key={id}>
              {/* @ts-ignore */}
              <ClickableDiv onClick={() => window.location.assign(url)}>
                <div
                  className="RelatedBreedCard flex flex-column ba b--subtle-gray br3 overflow-hidden z-0"
                  style={{ minWidth: '335px' }}
                >
                  <div className="aspect-ratio aspect-ratio--1x1">
                    <div className="aspect-ratio--object">
                      {/* @ts-ignore */}
                      <SmartImage src={photoUrl} {...smartImageProps} />
                    </div>
                  </div>
                  <div className="tc pv4 ph4">
                    <p className="RelatedBreedCard__breedName font-serif font-24 fw-medium">
                      {name}
                    </p>
                    {!window.__DPL_BOT_UA && availabilityCountText && (
                      <p className="font-16 secondary mt2">
                        {availabilityCountText}
                      </p>
                    )}
                  </div>
                  <div className="flex bt b--subtle-gray mt-auto">
                    <SmartLink
                      to={url}
                      className="font-16 fw-medium stone-700 tc pv4 ph4 flex-auto flex-basis-0"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      View puppies
                    </SmartLink>
                    {breedOverviewPageUrl && (
                      <SmartLink
                        to={breedOverviewPageUrl}
                        className="font-16 fw-medium stone-700 tc pv4 ph4 bl b--subtle-gray flex-auto flex-basis-0"
                        target="_blank"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        Learn about breed
                      </SmartLink>
                    )}
                  </div>
                </div>
              </ClickableDiv>
            </div>
          );
        })}
      </HorizontalSlider>
    </div>
  );
}

RelatedBreedsModule.defaultProps = {
  className: null,
  heading: 'Related breeds'
};
